import { Controller } from '@hotwired/stimulus'
import consumer from '~/channels/consumer'

/**
 * Order Progress Controller
 *
 * Subscribes to WebsiteOrderChannel for real-time order phase updates.
 * When admin transitions the order phase, this controller receives the
 * broadcast and updates the UI (or triggers a page reload for new phase content).
 */
export default class extends Controller {
  static values = {
    websiteId: String
  }

  connect() {
    console.log('[OrderProgress] Connected for website:', this.websiteIdValue)
    this.subscribeToChannel()
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  subscribeToChannel() {
    if (!this.websiteIdValue) {
      console.warn('[OrderProgress] No websiteId provided')
      return
    }

    this.subscription = consumer.subscriptions.create(
      { channel: 'WebsiteOrderChannel', website_id: this.websiteIdValue },
      {
        connected: () => {
          console.log('[OrderProgress] ActionCable connected')
        },
        disconnected: () => {
          console.log('[OrderProgress] ActionCable disconnected')
        },
        received: (data) => {
          console.log('[OrderProgress] Received:', data)
          this.handleBroadcast(data)
        }
      }
    )
  }

  handleBroadcast(data) {
    if (data.event === 'phase_changed') {
      this.handlePhaseChange(data)
    }
  }

  handlePhaseChange(data) {
    // Show a brief notification before reloading
    this.showUpdateNotification(data)

    // Reload the page to show new phase-specific content
    // Phase partials have complex conditional logic (buttons, previews, messages)
    // that are easier to handle with a full reload
    setTimeout(() => {
      window.location.reload()
    }, 1500)
  }

  showUpdateNotification(data) {
    // Create a toast notification
    const toast = document.createElement('div')
    toast.className = 'fixed bottom-4 right-4 z-50 bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 animate-fade-in'
    toast.innerHTML = `
      <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
      </svg>
      <div>
        <p class="font-semibold">Order Updated</p>
        <p class="text-sm opacity-90">Status changed to: ${data.phase_name}</p>
      </div>
    `

    document.body.appendChild(toast)

    // Remove after animation completes
    setTimeout(() => {
      toast.remove()
    }, 3000)
  }
}
